import React from "react";
import PropTypes from "prop-types";
import { PlusCircle, Trash } from "react-bootstrap-icons";
import FormSelect from "Common/Custom/FormSelect";
import FormInput from "Common/Custom/FormInput";
import FormMultiSelect from "../FormMultiSelect/FormMultiSelect";

/**
 * LaundryItemTable
 * Shared table component used by both AddLaundryForm and EditLaundryForm.
 *
 * Renders the dynamic item rows with:
 *  - Product dropdown (excludes already-selected products in other rows)
 *  - Task multi-select (filtered by selected product)
 *  - Quantity input (max = available stock for this row)
 *  - Total cost (read-only, auto-calculated)
 *  - Add / Delete row actions
 *
 * Props:
 *  - items                    {Array}    - Current item rows
 *  - itemFields               {Array}    - Field config from LaundryFields.json → item
 *  - errors                   {Object}   - Validation errors
 *  - TaskOptions              {Array}    - All task options from API
 *  - ProductOptions           {Array}    - All product options from API
 *  - originalQtyRef           {Ref}      - Saved quantities per product_id (edit mode)
 *  - handleItemChange         {Function}
 *  - addRow                   {Function}
 *  - deleteRow                {Function}
 *  - getAvailableProductOptions {Function}
 *  - grandTotal               {number}
 *  - isEditMode               {boolean}  - true = edit, false = add
 */
const LaundryItemTable = ({
    items,
    itemFields,
    errors,
    TaskOptions,
    ProductOptions,
    originalQtyRef,
    handleItemChange,
    addRow,
    deleteRow,
    getAvailableProductOptions,
    grandTotal,
    isEditMode,
}) => {
    const renderCell = (field, row, rowIndex) => {
        switch (field.name) {
            // ── Product dropdown ──────────────────────────────────────────
            case "product_id":
                return (
                    <FormSelect
                        field={field}
                        value={row.product_id}
                        options={getAvailableProductOptions(rowIndex).map((p) => ({
                            value: p.product_id,
                            label: p.product_name,
                        }))}
                        handleChange={(e) =>
                            handleItemChange(rowIndex, "product_id", e.target.value)
                        }
                        error={errors.items?.[rowIndex]?.product_id}
                    />
                );

            // ── Task multi-select (filtered by product) ───────────────────
            case "task_id":
                return (
                    <FormMultiSelect
                        field={field}
                        disabled={!row.product_id}
                        value={row.task_id || []}
                        options={TaskOptions.filter(
                            (t) => Number(t.product_id) === Number(row.product_id)
                        ).map((t) => ({
                            value: t.task_id,
                            label: `${t.task_name} (${Math.round(Number(t.price))})`,
                        }))}
                        handleChange={(e) =>
                            handleItemChange(rowIndex, "task_id", e.target.value)
                        }
                        error={errors.items?.[rowIndex]?.task_id}
                    />
                );

            // ── Quantity input ────────────────────────────────────────────
            case "quantity": {
                const product = ProductOptions.find(
                    (p) => Number(p.product_id) === Number(row.product_id)
                );
                const ready_quantity = product?.ready_quantity ?? 0;

                // Edit mode: max = ready + original saved qty (already in use by this record)
                // Add mode:  max = ready only
                const original_quantity = isEditMode
                    ? (originalQtyRef?.current?.[Number(row.product_id)] ?? 0)
                    : 0;
                const max_quantity = ready_quantity + original_quantity;

                return (
                    <FormInput
                        field={{
                            ...field,
                            type: "number",
                            max: max_quantity,
                            placeholder: `Available: ${max_quantity}`,
                            disabled: !row.product_id || !row.task_id?.length,
                            lableHide: true,
                        }}
                        value={row.quantity || ""}
                        handleChange={(e) =>
                            handleItemChange(rowIndex, "quantity", e.target.value)
                        }
                        error={errors.items?.[rowIndex]?.quantity}
                    />
                );
            }

            // ── Total cost (read-only) ────────────────────────────────────
            case "totle":
                return (
                    <FormInput
                        field={{ ...field, type: "number", disabled: true, lableHide: true }}
                        value={row.totle || 0}
                        error={errors.items?.[rowIndex]?.totle}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div className="col-12 mt-4">
            {typeof errors.items === "string" && (
                <div className="text-danger mb-2 small fw-bold">{errors.items}</div>
            )}
            <div className="table-responsive">
                <table className="table table-bordered align-middle">
                    <thead>
                        <tr>
                            <th colSpan="5" className="text-center">Laundry Items</th>
                        </tr>
                        <tr>
                            <th>Product</th>
                            <th>Task</th>
                            <th>Quantity</th>
                            <th>Total Cost</th>
                            <th width="120">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {itemFields.map((field) => (
                                    <td key={field.name}>
                                        {renderCell(field, row, rowIndex)}
                                    </td>
                                ))}
                                <td className="text-center">
                                    {/* Add row — disabled until current row is complete */}
                                    <button
                                        type="button"
                                        className="btn btn-outline-success btn-sm me-2"
                                        onClick={addRow}
                                        disabled={
                                            getAvailableProductOptions(rowIndex).length <= 1 ||
                                            !row.product_id ||
                                            !row.task_id?.length ||
                                            !row.quantity
                                        }
                                    >
                                        <PlusCircle size={16} />
                                    </button>
                                    {/* Delete row — disabled when only one row remains */}
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => deleteRow(rowIndex)}
                                        disabled={items.length <= 1}
                                    >
                                        <Trash size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={3} className="text-end fw-semibold">Grand Total</td>
                            <td colSpan={2} className="fw-semibold">
                                {grandTotal.toFixed(2)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

LaundryItemTable.propTypes = {
    items: PropTypes.array.isRequired,
    itemFields: PropTypes.array.isRequired,
    errors: PropTypes.object,
    TaskOptions: PropTypes.array.isRequired,
    ProductOptions: PropTypes.array.isRequired,
    originalQtyRef: PropTypes.object,
    handleItemChange: PropTypes.func.isRequired,
    addRow: PropTypes.func.isRequired,
    deleteRow: PropTypes.func.isRequired,
    getAvailableProductOptions: PropTypes.func.isRequired,
    grandTotal: PropTypes.number.isRequired,
    isEditMode: PropTypes.bool,
};

LaundryItemTable.defaultProps = {
    errors: {},
    originalQtyRef: { current: {} },
    isEditMode: false,
};

export default LaundryItemTable;
